import React, { useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import { useInjection } from "@presentation/components/dependencyInjection";
import { TeamManagementService } from "@application/useCases/workspace/TeamManagementService";
import { extractProblemDetails } from "@presentation/common/ErrorHelpers";
import type { TeamDetail } from "@domain/schemas/workspace";
import { useWorkspaceSuccessNotification } from "@presentation/components/notifications/useWorkspaceSuccessNotification";

interface RenameTeamDialogProps {
  teamId: string;
  currentName: string;
  onClose: () => void;
  onSuccess: (updated: TeamDetail) => void;
}

/**
Renaming the team itself - distinct from renaming one of the team's projects, which lives on that project's own row.
*/
export const RenameTeamDialog: React.FC<RenameTeamDialogProps> = ({ teamId, currentName, onClose, onSuccess }) => {
  const teamManagementService = useInjection(TeamManagementService);
  const notifySuccess = useWorkspaceSuccessNotification();
  const [name, setName] = useState(currentName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const canSubmit = name.trim().length > 0 && name.trim() !== currentName && !isSubmitting;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const updated = await teamManagementService.renameTeamAsync(teamId, name.trim());
      notifySuccess("team", "renamed");
      onSuccess(updated);
      onClose();
    } catch (error) {
      setErrorMessage(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not rename the team.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog title="Rename team" onClose={onClose} width={420}>
      <div style={{ display: "grid", gap: 16 }}>
        <div>
          <label htmlFor="rename-team-name" style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
            Team name
          </label>
          <Input id="rename-team-name" style={{ width: "100%" }} value={name} onChange={(event) => setName(String(event.value ?? ""))} />
        </div>
        {errorMessage && <div style={{ color: "#7f1d1d" }}>{errorMessage}</div>}
      </div>
      <DialogActionsBar>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button themeColor="primary" disabled={!canSubmit} onClick={handleSubmit}>
          Save
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};
